import { useSyncExternalStore } from "react";
import {
  AvatarChoice,
  EMPTY_AVATAR_CHOICE,
  normalizeAvatarChoice,
  resolveAvatarColor,
  resolveAvatarEmoji,
} from "@/engine/state/avatarChoice";

/**
 * The animal + color a participant picked for themselves, persisted locally and
 * shared with the room through presence/broadcast payloads.
 */
const STORAGE_KEY = "avatar-choice";

function load(): AvatarChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_AVATAR_CHOICE;
    return normalizeAvatarChoice(JSON.parse(raw));
  } catch {
    return EMPTY_AVATAR_CHOICE;
  }
}

let state: AvatarChoice =
  typeof window === "undefined" ? EMPTY_AVATAR_CHOICE : load();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): AvatarChoice {
  return state;
}

export function setAvatarChoice(patch: Partial<AvatarChoice>) {
  const next = normalizeAvatarChoice({ ...state, ...patch });
  if (next.emoji === state.emoji && next.color === state.color) return;
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable: keep in-memory only */
  }
  listeners.forEach((l) => l());
}

/**
 * Pins the appearance a visitor was first offered.
 *
 * Left unpinned, the animal and colour are derived from the tab's peer id —
 * which is deliberately forgotten when somebody leaves a room, so they would
 * come back as a different creature with a different name. Writing the first
 * suggestion down makes it theirs until they shuffle it themselves.
 */
export function ensureAvatarChoice(peerId: string): void {
  if (!peerId) return;
  if (state.emoji && state.color) return;
  setAvatarChoice({
    emoji: resolveAvatarEmoji(peerId, state.emoji),
    color: resolveAvatarColor(peerId, state.color),
  });
}

/** The current choice, readable outside React. */
export function getAvatarChoice(): AvatarChoice {
  return state;
}

export function useAvatarChoice(): AvatarChoice {
  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => EMPTY_AVATAR_CHOICE,
  );
}
